import { ReactNode, MouseEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCliftContext } from "../hooks/useCliftContext";
import { CliftReducerAction } from "../context/clift-context/clift-action-types";
import { ReactComponent as BackIcon } from "../assets/Back.svg";
import "./Page.css";

export interface PageProps {
  title: string;
  subtitle?: string;
  button?: ReactNode;
  children?: ReactNode;
  fromOriginalPage?: string;
}

const Page = ({
  title,
  subtitle,
  button,
  children,
  fromOriginalPage,
}: PageProps) => {
  const { t } = useTranslation();
  const { cliftState } = useCliftContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { dispatchCliftState } = useCliftContext();

  const setCapitalized = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const getLocalizedPath = (path: string) => {
    switch (path) {
      case "tenants":
        return cliftState.currentTenant?.tenantType === "ROOT"
          ? t("markets", { ns: "breadcrumb" })
          : t("lift_provders", { ns: "breadcrumb" });
      case "customers":
        return t("customers", { ns: "breadcrumb" });
      case "lifts":
        return t("lifts", { ns: "breadcrumb" });
      default:
        return setCapitalized(path);
    }
  };

  const isSafePathSegment = (segment: string): boolean =>
    /^[a-zA-Z0-9_-]+$/.test(segment);

  const getBreadcrumbs = () => {
    const paths = location.pathname.split("/").filter((x) => x);

    return paths.map((value, i) => {
      const last = i === paths.length - 1;
      const segments = paths.slice(0, i + 1);

      if (last || !segments.every(isSafePathSegment)) {
        return <span key={i}>{setCapitalized(title)}</span>;
      }

      const safeTo = `/${segments.map(encodeURIComponent).join("/")}`;

      return (
        <span key={i}>
          <Link to={safeTo}>{getLocalizedPath(value)}</Link> &gt;{" "}
        </span>
      );
    });
  };

  const isSubPage = () => {
    return location.pathname.split("/").length > 2;
  };

  const handleBack = (e: MouseEvent, fromOriginal: string) => {
    e.preventDefault();
    fromOriginal !== undefined
      ? navigate(fromOriginal)
      : navigate("..", { relative: "path", replace: true });
  };

  return (
    <div className="page">
      <div className="page-breadcrumb">{getBreadcrumbs()}</div>
      <header>
        {isSubPage() && (
          <button
            title="back"
            type="button"
            className="page-back"
            onClick={(e) => {
              handleBack(e, fromOriginalPage!);
              dispatchCliftState({
                type: CliftReducerAction.SetFromOriginalPage,
                fromOriginalPage: undefined,
              });
            }}
          >
            <BackIcon />
          </button>
        )}
        <div className="page-title-area ">
          <h1>{title}</h1>
          <h2>{subtitle}</h2>
        </div>
        <div className="page-button-area">{button}</div>
      </header>
      {children}
    </div>
  );
};

export default Page;
